class HeaderMegaMenu extends HTMLElement {
  constructor() {
    super();

    this.activeItem = null;
    this.openDelay = 80;
    this.closeDelay = 160;
    this.openTimers = new WeakMap();
    this.closeTimers = new WeakMap();

    this.onMediaChange = this.onMediaChange.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
  }

  connectedCallback() {
    this.header = this.closest('.header-wrapper');
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    this.items = Array.from(this.querySelectorAll('[data-mega-menu-item]'));

    if (!this.items.length) return;

    this.desktopMedia.addEventListener('change', this.onMediaChange);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    this.items.forEach((item) => this.bindItem(item));
    this.onMediaChange();
  }

  disconnectedCallback() {
    if (this.desktopMedia) this.desktopMedia.removeEventListener('change', this.onMediaChange);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    this.closeAll();
  }

  bindItem(item) {
    if (item.dataset.megaMenuBound === 'true') return;

    item.dataset.megaMenuBound = 'true';

    item.addEventListener('mouseenter', () => this.queueOpen(item));
    item.addEventListener('mouseleave', () => this.queueClose(item));

    item.addEventListener('focusin', () => {
      this.open(item);
    });

    item.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!item.contains(document.activeElement)) this.queueClose(item, 0);
      }, 0);
    });

    item.addEventListener('keydown', (event) => this.onItemKeyDown(event, item));
  }

  onItemKeyDown(event, item) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.close(item, true);
      return;
    }

    if (event.code.toUpperCase() !== 'ARROWDOWN') return;
    if (!event.target.matches('[data-mega-menu-trigger]')) return;

    event.preventDefault();
    this.open(item);

    const content = item.querySelector('[data-mega-menu-content]');
    const firstFocusableElement = content?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (firstFocusableElement) firstFocusableElement.focus();
  }

  onDocumentPointerDown(event) {
    if (!this.activeItem) return;
    if (this.contains(event.target)) return;

    this.closeAll();
  }

  onMediaChange() {
    if (this.isDesktop()) return;
    this.closeAll();
  }

  isDesktop() {
    return this.desktopMedia?.matches;
  }

  queueOpen(item) {
    if (!this.isDesktop()) return;

    this.clearTimer(this.closeTimers, item);

    const timer = window.setTimeout(() => {
      this.open(item);
    }, this.openDelay);

    this.openTimers.set(item, timer);
  }

  queueClose(item, delay = this.closeDelay) {
    this.clearTimer(this.openTimers, item);

    const timer = window.setTimeout(() => {
      this.close(item);
    }, delay);

    this.closeTimers.set(item, timer);
  }

  clearTimer(timerMap, item) {
    const timer = timerMap.get(item);
    if (timer) window.clearTimeout(timer);
    timerMap.delete(item);
  }

  open(item) {
    if (!this.isDesktop()) return;
    if (!item) return;

    this.clearTimer(this.openTimers, item);
    this.clearTimer(this.closeTimers, item);

    if (this.activeItem && this.activeItem !== item) {
      this.close(this.activeItem);
    }

    const trigger = item.querySelector('[data-mega-menu-trigger]');
    const content = item.querySelector('[data-mega-menu-content]');
    if (!trigger || !content) return;

    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    content.hidden = false;
    content.setAttribute('aria-hidden', 'false');
    this.activeItem = item;

    // Keep the sticky header visible while a desktop mega menu panel is open.
    if (this.header) this.header.preventHide = true;

    if (!this.header) return;
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;

    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  close(item, restoreFocus = false) {
    if (!item) return;

    this.clearTimer(this.openTimers, item);
    this.clearTimer(this.closeTimers, item);

    const trigger = item.querySelector('[data-mega-menu-trigger]');
    const content = item.querySelector('[data-mega-menu-content]');
    if (!trigger || !content) return;

    item.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    content.hidden = true;

    if (restoreFocus) trigger.focus();
    if (this.activeItem === item) this.activeItem = null;

    if (!this.activeItem && this.header) this.header.preventHide = false;
  }

  closeAll() {
    this.items?.forEach((item) => this.close(item));
  }
}

customElements.define('header-mega-menu', HeaderMegaMenu);
